"use client";

import { useState, useEffect } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useTranslation } from "next-i18next";
import { motion } from "framer-motion";
import {
  Grid,
  TextField,
  FormControl,
  MenuItem,
  Select,
  InputLabel,
  FormControlLabel,
  Checkbox,
  Radio,
  RadioGroup,
  FormLabel,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { enqueueSnackbar } from "notistack";
import { PersonAdd, People, Laptop, Group } from "@mui/icons-material";
import Image from "next/image";
import GeneralService from "@/core/services/GeneralService";
import { SupportRequest } from "@/core/models/General.model";
import { parseJsonBody } from "@/lib/general.utils";
import useGeneralStore from "@/store/General/useGeneralStore";
import { PhoneCodeSelector } from "@/components/General/PhoneCodeSelector";

const VolunteerComponent = () => {
  const { t, i18n } = useTranslation("common");
  const isRtl = i18n.language === "ar";
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [phoneCode, setPhoneCode] = useState("+20");

  const { VolunteerComponent: volunteerData, GovernoratesComponent } =
    useGeneralStore((state) => ({
      VolunteerComponent: state.VolunteerComponent,
      GovernoratesComponent: state.GovernoratesComponent,
    }));

  const volunteerJson = parseJsonBody(volunteerData?.jsonBody);
  const governoratesJson = GovernoratesComponent
    ? parseJsonBody(GovernoratesComponent.jsonBody)
    : null;
  const governorates = governoratesJson?.governorates || [];

  // Fetch volunteer component on mount

  // Get data from database or fallback to defaults
  const header = volunteerJson?.header;
  const introText = volunteerJson?.introText;
  const benefits = volunteerJson?.benefits;
  const activities = volunteerJson?.activities || [];
  const galleryImages = volunteerJson?.galleryImages || [];

  // Fetch governorates on mount

  const validationSchema = Yup.object({
    name: Yup.string()
      .required(t("volunteer.nameRequired"))
      .min(2, t("volunteer.nameInvalid")),
    dateOfBirth: Yup.string().required(t("volunteer.dateOfBirthRequired")),
    email: Yup.string()
      .email(t("volunteer.emailInvalid"))
      .required(t("volunteer.emailRequired")),
    phone: Yup.string()
      .required(t("volunteer.phoneRequired"))
      .min(8, t("volunteer.phoneInvalid")),
    governorate: Yup.string().required(t("volunteer.governorateRequired")),
    activities: Yup.array()
      .min(1, t("volunteer.activitiesRequired"))
      .required(t("volunteer.activitiesRequired")),
    academicQualification: Yup.string().required(
      t("volunteer.academicQualificationRequired")
    ),
    professionalExperience: Yup.string(),
    volunteerExperience: Yup.string(),
    canParticipateOutside: Yup.string().required(
      t("volunteer.canParticipateOutsideRequired")
    ),
  });

  const formik = useFormik({
    initialValues: {
      name: "",
      dateOfBirth: "",
      email: "",
      phone: "",
      governorate: "",
      activities: [] as string[],
      academicQualification: "",
      professionalExperience: "",
      volunteerExperience: "",
      canParticipateOutside: "",
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        setIsSubmitting(true);
        const supportData: Omit<SupportRequest, "id" | "insertDate"> = {
          name: values.name,
          email: values.email,
          phone: `${phoneCode}${values.phone}`,
          subject: "Volunteer Application",
          body: JSON.stringify({
            dateOfBirth: values.dateOfBirth,
            governorate: values.governorate,
            activities: values.activities,
            academicQualification: values.academicQualification,
            professionalExperience: values.professionalExperience,
            volunteerExperience: values.volunteerExperience,
            canParticipateOutside: values.canParticipateOutside,
          }),
        };

        const response = await GeneralService.AddSupport("Volunteer", {
          ...supportData,
          id: 0,
          insertDate: "",
        } as SupportRequest);

        if (response?.success) {
          enqueueSnackbar(
            t("volunteer.success") || "Application submitted successfully!",
            {
              variant: "success",
            }
          );
          formik.resetForm();
        } else {
          throw new Error(response?.message || "Failed to submit application");
        }
      } catch (error: any) {
        console.error("Error submitting volunteer form:", error);
        enqueueSnackbar(
          error?.message ||
            t("volunteer.error") ||
            "Failed to submit application. Please try again.",
          {
            variant: "error",
          }
        );
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  const handleActivityChange = (activityId: string) => {
    const currentActivities = formik.values.activities;
    const isSelected = currentActivities.includes(activityId);

    if (isSelected) {
      formik.setFieldValue(
        "activities",
        currentActivities.filter((id) => id !== activityId)
      );
    } else {
      formik.setFieldValue("activities", [...currentActivities, activityId]);
    }
  };

  const getBenefitIcon = (iconName: string) => {
    switch (iconName) {
      case "People":
        return <People className="text-[2rem] md:text-[2.5rem] text-primary" />;
      case "Laptop":
        return <Laptop className="text-[2rem] md:text-[2.5rem] text-primary" />;
      case "Group":
        return <Group className="text-[2rem] md:text-[2.5rem] text-primary" />;
      default:
        return <People className="text-[2rem] md:text-[2.5rem] text-primary" />;
    }
  };

  return (
    <div className="bg-gray-100 ">
      {/* Header Section */}
      <section className="bg-gray-100 py-[2rem]  px-[2rem]">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="flex items-center gap-[1rem] mb-[1.5rem]"
            dir={isRtl ? "rtl" : "ltr"}
          >
            <PersonAdd className="text-primary text-[2rem] md:text-[2.5rem]" />
            <div>
              <h1 className="text-[1.5rem] md:text-[2.5rem] font-bold text-primary">
                {header?.title}
              </h1>
            </div>
          </motion.div>

          {/* Intro Text */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="my-[2.5rem] bg-gray-100  rounded-lg"
            dir={isRtl ? "rtl" : "ltr"}
          >
            <p className="text-[1rem] md:text-[1.2rem] text-gray-800 leading-relaxed">
              {introText}
            </p>
          </motion.div>

          {/* Benefits Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="grid grid-cols-1 md:grid-cols-3 gap-[2rem] "
          >
            {[benefits?.benefit1, benefits?.benefit2, benefits?.benefit3].map(
              (benefit, index) => (
                <div
                  key={index}
                  className="flex items-center gap-[1rem]"
                  dir={isRtl ? "rtl" : "ltr"}
                >
                  {getBenefitIcon(benefit?.icon)}
                  <p className="text-[1rem] md:text-[1.1rem] text-gray-800 font-medium">
                    {benefit?.text}
                  </p>
                </div>
              )
            )}
          </motion.div>

        </div>
      </section>

      {/* Form Section */}
      <section className="bg-white py-[3rem] ">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <form
              onSubmit={formik.handleSubmit}
              className="w-full"
              dir={isRtl ? "rtl" : "ltr"}
            >
              <Grid container>
                {/* Name Field */}
                <Grid item xs={12}>
                  <FormControl
                    fullWidth
                    required
                    error={formik.touched.name && Boolean(formik.errors.name)}
                  >
                    <TextField
                      id="name"
                      name="name"
                      label={t("volunteer.namePlaceholder")}
                      placeholder={t("volunteer.namePlaceholder")}
                      size="small"
                      required
                      InputLabelProps={{ shrink: true }}
                      value={formik.values.name}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={formik.touched.name && Boolean(formik.errors.name)}
                      helperText={
                        formik.touched.name && (formik.errors.name as string)
                      }
                      className="rounded-lg"
                      inputProps={{ dir: isRtl ? "rtl" : "ltr" }}
                    />
                  </FormControl>
                </Grid>

                {/* Date of Birth Field */}
                <Grid item xs={12}>
                  <FormControl
                    fullWidth
                    required
                    error={
                      formik.touched.dateOfBirth &&
                      Boolean(formik.errors.dateOfBirth)
                    }
                  >
                    <TextField
                      id="dateOfBirth"
                      name="dateOfBirth"
                      label={t("volunteer.dateOfBirthPlaceholder")}
                      placeholder={t("volunteer.dateOfBirthPlaceholder")}
                      type="date"
                      size="small"
                      required
                      InputLabelProps={{ shrink: true }}
                      value={formik.values.dateOfBirth}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={
                        formik.touched.dateOfBirth &&
                        Boolean(formik.errors.dateOfBirth)
                      }
                      helperText={
                        formik.touched.dateOfBirth &&
                        (formik.errors.dateOfBirth as string)
                      }
                      className="rounded-lg"
                      inputProps={{ dir: "ltr" }}
                    />
                  </FormControl>
                </Grid>

                {/* Email Field */}
                <Grid item xs={12}>
                  <FormControl
                    fullWidth
                    required
                    error={formik.touched.email && Boolean(formik.errors.email)}
                  >
                    <TextField
                      id="email"
                      name="email"
                      label={t("volunteer.emailPlaceholder")}
                      placeholder={t("volunteer.emailPlaceholder")}
                      type="email"
                      size="small"
                      required
                      InputLabelProps={{ shrink: true }}
                      value={formik.values.email}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={
                        formik.touched.email && Boolean(formik.errors.email)
                      }
                      helperText={
                        formik.touched.email && (formik.errors.email as string)
                      }
                      className="rounded-lg"
                      inputProps={{ dir: "ltr" }}
                    />
                  </FormControl>
                </Grid>

                {/* Phone Field */}
                <Grid item xs={12}>
                  <FormControl
                    fullWidth
                    required
                    error={formik.touched.phone && Boolean(formik.errors.phone)}
                  >
                    <div className="flex gap-2">
                      <div className="w-[8rem]">
                        <PhoneCodeSelector
                          value={phoneCode}
                          onChange={setPhoneCode}
                        />
                      </div>
                      <div className="flex-1">
                        <TextField
                          id="phone"
                          name="phone"
                          label={t("volunteer.phonePlaceholder")}
                          placeholder={t("volunteer.phonePlaceholder")}
                          type="tel"
                          size="small"
                          required
                          fullWidth
                          InputLabelProps={{ shrink: true }}
                          value={formik.values.phone}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          error={
                            formik.touched.phone && Boolean(formik.errors.phone)
                          }
                          helperText={
                            formik.touched.phone &&
                            (formik.errors.phone as string)
                          }
                          className="rounded-lg"
                          inputProps={{ dir: "ltr" }}
                        />
                      </div>
                    </div>
                  </FormControl>
                </Grid>

                {/* Governorate Field */}
                <Grid item xs={12}>
                  <FormControl
                    fullWidth
                    required
                    error={
                      formik.touched.governorate &&
                      Boolean(formik.errors.governorate)
                    }
                  >
                    <Select
                      notched={true}
                      id="governorate"
                      name="governorate"
                      labelId="governorate-label"
                      value={formik.values.governorate}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={
                        formik.touched.governorate &&
                        Boolean(formik.errors.governorate)
                      }
                      size="small"
                      className="rounded-lg"
                      displayEmpty
                    >
                      <MenuItem value="" disabled>
                        <em>{t("volunteer.governoratePlaceholder")}</em>
                      </MenuItem>
                      {governorates.map((gov: any) => (
                        <MenuItem key={gov.id} value={gov.title}>
                          {gov.title}
                        </MenuItem>
                      ))}
                    </Select>
                    {formik.touched.governorate &&
                      formik.errors.governorate && (
                        <div className="text-[#e57d7a] text-sm mt-1">
                          {formik.errors.governorate as string}
                        </div>
                      )}
                  </FormControl>
                </Grid>

                {/* Activities Field */}
                <Grid item xs={12}>
                  <FormControl
                    fullWidth
                    required
                    error={
                      formik.touched.activities &&
                      Boolean(formik.errors.activities)
                    }
                  >
                    <FormLabel
                      component="legend"
                      className="mb-2 text-[0.875rem] font-medium"
                    >
                      {t("volunteer.activitiesPlaceholder")} *
                    </FormLabel>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {activities.map((activity: any) => (
                        <FormControlLabel
                          key={activity.id}
                          control={
                            <Checkbox
                              checked={formik.values.activities.includes(
                                activity.id
                              )}
                              onChange={() => handleActivityChange(activity.id)}
                              onBlur={formik.handleBlur}
                            />
                          }
                          label={activity.label}
                          className="m-0"
                        />
                      ))}
                    </div>
                    {formik.touched.activities && formik.errors.activities && (
                      <div className="text-[#e57d7a] text-sm mt-1">
                        {formik.errors.activities as string}
                      </div>
                    )}
                  </FormControl>
                </Grid>

                {/* Academic Qualification Field */}
                <Grid item xs={12}>
                  <FormControl
                    fullWidth
                    required
                    error={
                      formik.touched.academicQualification &&
                      Boolean(formik.errors.academicQualification)
                    }
                  >
                    <TextField
                      id="academicQualification"
                      name="academicQualification"
                      label={t("volunteer.academicQualificationPlaceholder")}
                      placeholder={t(
                        "volunteer.academicQualificationPlaceholder"
                      )}
                      multiline
                      rows={4}
                      size="small"
                      required
                      InputLabelProps={{ shrink: true }}
                      value={formik.values.academicQualification}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={
                        formik.touched.academicQualification &&
                        Boolean(formik.errors.academicQualification)
                      }
                      helperText={
                        formik.touched.academicQualification &&
                        (formik.errors.academicQualification as string)
                      }
                      className="rounded-lg"
                      inputProps={{ dir: isRtl ? "rtl" : "ltr" }}
                    />
                  </FormControl>
                </Grid>

                {/* Professional Experience Field */}
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <TextField
                      id="professionalExperience"
                      name="professionalExperience"
                      label={t("volunteer.professionalExperiencePlaceholder")}
                      placeholder={t(
                        "volunteer.professionalExperiencePlaceholder"
                      )}
                      multiline
                      rows={4}
                      size="small"
                      InputLabelProps={{ shrink: true }}
                      value={formik.values.professionalExperience}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      className="rounded-lg"
                      inputProps={{ dir: isRtl ? "rtl" : "ltr" }}
                    />
                  </FormControl>
                </Grid>

                {/* Volunteer/Sports Experience Field */}
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <TextField
                      id="volunteerExperience"
                      name="volunteerExperience"
                      label={t("volunteer.volunteerExperiencePlaceholder")}
                      placeholder={t(
                        "volunteer.volunteerExperiencePlaceholder"
                      )}
                      multiline
                      rows={4}
                      size="small"
                      InputLabelProps={{ shrink: true }}
                      value={formik.values.volunteerExperience}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      className="rounded-lg"
                      inputProps={{ dir: isRtl ? "rtl" : "ltr" }}
                    />
                  </FormControl>
                </Grid>

                {/* Can Participate Outside Cairo Field */}
                <Grid item xs={12}>
                  <FormControl
                    fullWidth
                    required
                    error={
                      formik.touched.canParticipateOutside &&
                      Boolean(formik.errors.canParticipateOutside)
                    }
                  >
                    <FormLabel
                      component="legend"
                      className="mb-2 text-[0.875rem] font-medium"
                    >
                      {t("volunteer.canParticipateOutsidePlaceholder")} || *
                    </FormLabel>
                    <RadioGroup
                      value={formik.values.canParticipateOutside}
                      onChange={(e) =>
                        formik.setFieldValue(
                          "canParticipateOutside",
                          e.target.value
                        )
                      }
                      onBlur={formik.handleBlur}
                      row={false}
                    >
                      <FormControlLabel
                        value="yes"
                        control={<Radio />}
                        label={t("volunteer.yes")}
                      />
                      <FormControlLabel
                        value="no"
                        control={<Radio />}
                        label={t("volunteer.no")}
                      />
                    </RadioGroup>
                    {formik.touched.canParticipateOutside &&
                      formik.errors.canParticipateOutside && (
                        <div className="text-[#e57d7a] text-sm mt-1">
                          {formik.errors.canParticipateOutside as string}
                        </div>
                      )}
                  </FormControl>
                </Grid>

                {/* Submit Button */}
                <Grid item xs={12}>
                  <LoadingButton
                    type="submit"
                    fullWidth
                    variant="contained"
                    loading={isSubmitting}
                    className="rounded-lg bg-secondary hover:bg-secondary/80 py-[1rem] text-[1rem] font-semibold text-white normal-case"
                  >
                    {t("volunteer.submit")}
                  </LoadingButton>
                </Grid>
              </Grid>
            </form>
          </motion.div>
        </div>
      </section>

      {/* Image Gallery Section */}
      {galleryImages.length > 0 && (
        <section className="bg-gray-100 py-[3rem] md:py-[5rem] px-[2rem] w-full">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-2 gap-[1rem] md:gap-[2rem]">
              {galleryImages.slice(0, 4).map((image: any, index: number) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="relative aspect-square rounded-lg overflow-hidden"
                >
                  <Image
                    src={image.url || image || ""}
                    alt={`Gallery image ${index + 1}`}
                    fill
                    className="object-cover"
                  />
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default VolunteerComponent;
